/**
 * In-game levelling: the board clock STARTS generous and tightens by 5 points a star.
 *
 * The problem this solves: for a strong player the game had no ceiling and no rungs — boards ran
 * until the hearts were gone and never got harder, so a good session felt endless. Stars are the
 * rung, shown as a solar system filling up the right edge (src/game/ui/PlanetStack.ts) and
 * cashed in here as speed.
 *
 * The curve starts ABOVE 1. Tightening from the normal clock made the opening boards harder than
 * they had ever been, which is exactly the wrong end to squeeze for a young child: the first
 * minute of a game is where they are still finding their feet. So the first board runs at 125% of
 * the usual clock and only an accomplished run works its way back down past 100% — difficulty is
 * something the player earns, not something they are handed.
 *
 * Two further properties:
 *  - It counts stars earned THIS GAME, not the lifetime total, so every game starts from the same
 *    generous clock and an advanced learner is never permanently punished for past progress. The
 *    journey's own star accounting is not touched by any of this.
 *  - Linear, not compounding. The old rate compounded because a flat subtraction would have walked
 *    the clock to zero; with a floor only nine steps below the start, plain subtraction is both
 *    safe and easier to reason about — 125%, 120%, 115% … 80%.
 */
import { CRUMBLE, STAR_STACK_SLOTS } from '@/game/config';

/**
 * Score that fills the whole stack, and the points each star therefore costs.
 *
 * The stack used to draw the *journey* stars, and that could not work: a journey star needs
 * mastery, mastery needs MIN_SESSIONS_TO_MASTER sessions, so a first-session player could never
 * earn one however well they played — the meter climbed to its cap after five boards and then sat
 * frozen for the rest of the game. The in-game stack is its own thing now, counted in the score
 * the player can already see, and the lifetime journey (stars, UFOs, the ProgressBar) is untouched.
 *
 * Points rather than a private click counter so the two readouts agree: at SCORING.correctHit=10 a
 * smash is 10 points, i.e. 8% of a star, so the fill visibly moves on every single correct answer.
 * Derived from STAR_STACK_SLOTS so the ceiling and the number of slots cannot drift apart.
 */
export const FULL_STACK_POINTS = 1125;
export const POINTS_PER_STAR = FULL_STACK_POINTS / STAR_STACK_SLOTS;   // 125

/** Whole stars and how far the next one has filled, from this game's score. */
export function starProgress(
  score: number,
  perStar: number = POINTS_PER_STAR,
): { stars: number; fraction: number } {
  if (!Number.isFinite(score) || score <= 0 || perStar <= 0) return { stars: 0, fraction: 0 };
  return { stars: Math.floor(score / perStar), fraction: (score % perStar) / perStar };
}

/**
 * Board clock before any star is earned: a quarter longer than the plain rule. The opening boards
 * are where a child is least settled, so that is where the game should be kindest.
 */
export const START_TIMEOUT_MULT = 1.25;

/**
 * Board clock tightening per star earned this game, subtracted from START_TIMEOUT_MULT. 5 points,
 * down from the 10% it began as: once the stack became points-driven, stars arrive far faster than
 * when each one needed mastery, and the clock was being slammed into its floor mid-game.
 */
export const SPEEDUP_PER_STAR = 0.05;

/**
 * Floor on the multiplier: 80% of the plain clock, reached at nine stars. A full 8-star stack
 * lands at 85%, so even the run that fills the whole stack never plays faster than a fifth under
 * the old baseline — the ceiling is a challenge, not a punishment.
 */
export const MIN_TIMEOUT_MULT = 0.8;

/**
 * Multiplier to apply to the crumbling-board threshold for a game in which `sessionStars` have
 * been earned. Above 1 = more time than the plain rule; below 1 = the board crumbles sooner.
 */
export function timeoutMultFor(sessionStars: number): number {
  const stars = Number.isFinite(sessionStars) && sessionStars > 0 ? Math.floor(sessionStars) : 0;
  return Math.max(MIN_TIMEOUT_MULT, START_TIMEOUT_MULT - SPEEDUP_PER_STAR * stars);
}

/**
 * A trim on the finished clock — everybody gets a little less time than the pace-and-spread rule
 * alone would give them, and whoever needs less time per board gets a little less still.
 *
 * ── Why this is a separate multiplier and not just a smaller `k` ──────────────
 * `k` is measured in the player's OWN deviations, so a change to it is worth
 * `exp(Δk × spread)` — which is a different amount of time for every player, and *least* for the
 * steadiest. Scaling `k` by 0.95 takes 5.4% off a first-grader's clock and **0.7% off an adult's**.
 * Anything expressed as "everyone gets x% less time" therefore has to multiply the threshold, after
 * the exponent has done its work. The mode floors still win underneath, as always.
 *
 * That is also a defect in `timeoutMultFor` above, stated plainly: because levelling scales `k`, the
 * whole nine-star ladder is worth 33% of a scattered player's clock and 7% of a metronomic one's.
 * The ladder is not wrong — `k` is the right place to express "the same statistical dare for
 * everyone" — but it cannot express "and everyone plays a little tighter now", which is what this is.
 *
 * ── Why the grading is keyed on the PACE ──────────────────────────────────────
 * A player who clears a board in 40s gets `clockTrimSlow`; one who needs 8s gets `clockTrimFast`,
 * which is a little smaller. This is the first thing in the mechanic that reads the player's
 * ABSOLUTE speed, and it is a deliberate, one-sided break with the scale invariance everything else
 * rests on (specs/adaptive-difficulty.md §1.1, §7): a slow player is never charged for being slow —
 * they get the *smallest* trim — the rule only declines to be quite as generous to someone who has
 * shown they do not need the room. "The child is too slow for the game" stays impossible.
 *
 * It is also the only key that separates the players who actually need separating. Stars are
 * throughput, so a strong first-grader eventually crosses them; spread is what §6 already uses and
 * real humans of every age turn out to share. Seconds per board do not overlap at all — 40-55s
 * observed for a beginning reader against 3-9s for an adult.
 *
 * Log-interpolated: board times are multiplicative, so the trim should move by the same amount for
 * each halving of the pace wherever on the scale you start.
 *
 * The numbers live in `CRUMBLE` (config.ts) with the rest of the crumble tuning, so the sweep can
 * price candidates the same way it prices `spreadK` and the press. Always returns ≤ 1.
 */
export function clockTrimFor(paceMs: number | null): number {
  const { clockTrimSlow, clockTrimSlowMs, clockTrimFast, clockTrimFastMs } = CRUMBLE;
  // No pace yet means no clock yet either (the mechanic is inactive), so the value is moot — but be
  // generous rather than clever if that ever stops being true.
  if (paceMs === null || !Number.isFinite(paceMs) || paceMs <= 0) return clockTrimSlow;
  if (paceMs >= clockTrimSlowMs) return clockTrimSlow;
  if (paceMs <= clockTrimFastMs) return clockTrimFast;
  const t = Math.log(paceMs / clockTrimFastMs) / Math.log(clockTrimSlowMs / clockTrimFastMs);
  return clockTrimFast + t * (clockTrimSlow - clockTrimFast);
}
